import React, { useEffect } from 'react';
import { IonPage, IonIcon } from '@ionic/react';
import { arrowBackCircle } from 'ionicons/icons';
import { useHistory, useLocation } from 'react-router-dom';
import { mapStackTrace } from 'sourcemapped-stacktrace';

/** État géré par l'ErrorBoundary */
interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
  stack?: string;
  name?: string;
}

/** Props de l'ErrorBoundary */
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * mapStackTrace est fourni sous forme de callback.
 * On le wrappe dans une Promise pour pouvoir l'utiliser en async/await.
 */
async function mapStackTraceAsync(stack: string): Promise<string> {
  return await new Promise((resolve) => {
    mapStackTrace(stack, (mappedStack: string[]) => {
      // On renvoie la stack reconstituée sous forme de string
      resolve(mappedStack.join('\n'));
    });
  });
}

/**
 * Fonction utilitaire asynchrone qui normalise l'erreur ET transforme la stack via mapStackTrace.
 */
async function normalizeErrorAsync(error: unknown): Promise<{ message: string; stack?: string; name?: string }> {
  let message = '';
  let stack: string | undefined;
  let name: string | undefined;

  if (error instanceof Error) {
    message = error.message;
    name = error.name;
    if (error.stack != null) {
      stack = await mapStackTraceAsync(error.stack);
    }
  } else if (typeof error === 'string') {
    message = error;
    name = 'Error';
  } else if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, any>;

    // Vérification explicite de la présence d'une réponse avec data
    if (errObj.response?.data != null) {
      if (typeof errObj.response.data === 'object') {
        if (
          errObj.response.data.message !== undefined &&
          errObj.response.data.message !== null &&
          typeof errObj.response.data.message === 'string'
        ) {
          message = errObj.response.data.message;
        } else if (
          errObj.response.data.messsage !== undefined &&
          errObj.response.data.messsage !== null &&
          typeof errObj.response.data.messsage === 'string'
        ) {
          // parfois on voit des typos "messsage"
          message = errObj.response.data.messsage;
        }
      } else if (typeof errObj.response.data === 'string') {
        message = errObj.response.data;
      }
    }

    // Si aucun message n'a été trouvé via response, on vérifie la propriété message
    if (
      message === '' &&
      errObj.message !== undefined &&
      errObj.message !== null &&
      typeof errObj.message === 'string'
    ) {
      message = errObj.message;
    }

    // En dernier recours, on essaie de convertir l'erreur en JSON
    if (message === '') {
      try {
        message = JSON.stringify(error);
      } catch {
        message = 'Une erreur inconnue est survenue';
      }
    }

    // Vérification explicite de la stack
    if (errObj.stack !== undefined && errObj.stack !== null && typeof errObj.stack === 'string') {
      stack = await mapStackTraceAsync(errObj.stack);
    }

    // Vérification explicite du nom de l'erreur
    if (errObj.name !== undefined && errObj.name !== null && typeof errObj.name === 'string') {
      name = errObj.name;
    } else {
      name = 'Error';
    }
  } else {
    // Cas inconnu
    message = 'Une erreur inconnue est survenue';
    name = 'Error';
  }

  return { message, stack, name };
}

/**
 * Tronque les URLs dans une stack trace pour ne conserver que la partie à partir de "/src/".
 */
function shortenStackTrace(stack: string): string {
  return stack
    .split('\n')
    .map(line => line.replace(/^(.*?)(\/src\/.*)/, '$2'))
    .join('\n');
}

/**
 * Composant de fallback affiché en cas d'erreur.
 */
const FallbackUI: React.FC<{ error: ErrorBoundaryState; onReset: () => void }> = ({ error, onReset }) => {
  const history = useHistory();
  const location = useLocation();

  console.error('Erreur capturée (affichée dans le fallback) :', error);

  useEffect(() => {
    // Possibilité d'ajouter un reload conditionnel, un log vers un service, etc.
  }, [error.message]);

  return (
    <IonPage className="bg-white">
      <div className="flex flex-col min-h-screen bg-white h-screen">
        <div className="flex-grow flex flex-col items-center justify-start p-5">
          <p className="text-ionic-light bg-ionic-primary p-4 rounded-lg text-base my-5 whitespace-pre-wrap min-w-32 text-center">
            {error.message}
          </p>
          {error.stack != null && (
            <pre className="text-left text-ionic-primary bg-ionic-light h-1/2 p-4 rounded-lg w-10/12 whitespace-pre-wrap">
              {shortenStackTrace(error.stack)}
            </pre>
          )}
          <div
            className="bg-ionic-secondary text-white py-2 px-4 rounded flex items-center cursor-pointer mt-5"
            onClick={() => {
              onReset();
              history.push(history.location.pathname, location.state);
            }}
          >
            <IonIcon icon={arrowBackCircle} className="mr-2" />
            Retour
          </div>
        </div>
      </div>
    </IonPage>
  );
};

/**
 * ErrorBoundary qui capture :
 * - les erreurs de rendu via getDerivedStateFromError / componentDidCatch,
 * - les erreurs globales (window.addEventListener 'error'),
 * - les rejets de promesses non gérés (window.addEventListener 'unhandledrejection').
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: '', stack: undefined, name: '' };
  }

  componentDidMount() {
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  /**
   * Cette méthode est synchrone et permet à React de basculer
   * immédiatement vers le fallback. On ne peut pas y faire d'appel async.
   */
  static getDerivedStateFromError(): ErrorBoundaryState {
    // On déclenche simplement l'état d'erreur ; la stack sera ensuite affinée dans componentDidCatch
    return { hasError: true, message: '', stack: undefined, name: '' };
  }

  /**
   * Après que React ait basculé l'UI en fallback, on peut faire
   * l'appel async pour récupérer la stack reconstituée et mettre à jour le state.
   */
  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    console.error('Erreur capturée dans componentDidCatch :', error, errorInfo);

    // On mappe l'erreur de façon asynchrone
    void normalizeErrorAsync(error).then(({ message, stack, name }) => {
      this.setState({ hasError: true, message, stack, name });
    });
    // Ici, vous pouvez également envoyer l'erreur à un service externe (Sentry, etc.)
  }

  /**
   * Capture explicite des erreurs globales synchrones
   */
  private readonly handleGlobalError = (event: ErrorEvent): boolean => {
    // Pour éviter que le navigateur n'affiche son message d'erreur par défaut
    event.preventDefault();

    if (event.error !== undefined) {
      void normalizeErrorAsync(event.error).then(({ message, stack, name }) => {
        console.error('Erreur globale capturée :', event.error);
        this.setState({ hasError: true, message, stack, name });
      });
    }
    return true;
  };

  /**
   * Capture explicite des rejets de promesses non gérés (asynchrones)
   */
  private readonly handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    event.preventDefault();

    if (event.reason !== undefined) {
      void normalizeErrorAsync(event.reason).then(({ message, stack, name }) => {
        console.error('Rejet non géré capturé :', event.reason);
        this.setState({ hasError: true, message, stack, name });
      });
    }
  };

  /**
   * Permet de réinitialiser l'état d'erreur et de retenter le rendu normal.
   */
  resetError = () => {
    this.setState({ hasError: false, message: '', stack: undefined, name: '' });
  };

  render() {
    return this.state.hasError ? (
      <FallbackUI error={this.state} onReset={this.resetError} />
    ) : (
      this.props.children
    );
  }
}

export default ErrorBoundary;
